import { baseUrl, csrfToken } from './http.js';

export async function getToken(callback) {
    const response = await fetch(`${baseUrl()}checkout/token`, {
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    const data = await response.json();

    if (callback) callback(data.token);

    return data.token;
}

export async function getSession(id) {
    const response = await fetch(`${baseUrl()}checkout/session/${id}?_=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
        const error = new Error(`Polling failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }

    return response.json();
}

export async function getTotal(checkoutSessionType, form, overrideTotals = false) {
    const data = new URLSearchParams(new FormData(form));

    data.append('checkout_session_type', checkoutSessionType);

    if (overrideTotals) {
        data.append('override_totals', '1');
    }

    const response = await fetch(`${baseUrl()}checkout/total`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: data,
    });

    return response.json();
}

export async function upsertSession(checkoutSessionType, form) {
    const data = new URLSearchParams(new FormData(form));

    data.append('checkout_session_type', checkoutSessionType);

    return fetch(`${baseUrl()}checkout/session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: data,
    });
}

export async function uploadAudioMessage(checkoutSessionId, voucherKey, audioBlob) {
    const formData = new FormData();

    formData.append('audio_message', audioBlob);
    formData.append('checkout_session_id', checkoutSessionId);
    formData.append('voucher_key', voucherKey);

    const response = await fetch(`${baseUrl()}checkout/media`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
    });

    return response.json();
}

export async function previewVoucher(model) {
    const response = await fetch(`${baseUrl()}voucher/preview/${model.voucher_id}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
            audio_url: model.audio_url || '',
            delivery_method: model.delivery,
            message: model.message,
            name: model.name,
            option_id: model.option_id,
            packaging_id: model.packaging,
            prefix: model.prefix,
            reward_offer_id: model.reward_offer_id || null,
            valid_at: model.valid_at,
            voucher_style: model.voucher_style || '',
        }),
    });

    return response.json();
}

export async function applyPromoCode(checkoutSessionId, code, contactEmail) {
    const data = new URLSearchParams();

    data.append('checkout_session_id', checkoutSessionId);
    data.append('code', code);

    if (contactEmail) {
        data.append('contact_email', contactEmail);
    }

    const response = await fetch(`${baseUrl()}promo`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: data,
    });

    return response.json();
}

export async function getTotalJson(payload) {
    const response = await fetch(`${baseUrl()}checkout/total`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
    });

    return response.json();
}

export async function applyPromoCodeJson(payload) {
    const response = await fetch(`${baseUrl()}promo`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
    });

    return response.json();
}

export async function submit(form, data) {
    const response = await fetch(form.getAttribute('action'), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: data,
    });

    if (!response.ok) {
        let responseData;

        try {
            responseData = await response.json();
        } catch {
            responseData = {
                messages: ['Something went wrong. Please refresh the page and try again.'],
                status: 'SERVER_ERROR',
            };
        }

        responseData._debug = {
            httpStatus: response.status,
            timestamp: new Date().toISOString(),
            url: response.url,
        };

        throw responseData;
    }

    return response.json();
}

export async function submitJson(actionUrl, payload) {
    const response = await fetch(actionUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        let responseData;

        try {
            responseData = await response.json();
        } catch {
            responseData = {
                messages: ['Something went wrong. Please refresh the page and try again.'],
                status: 'SERVER_ERROR',
            };
        }

        responseData._debug = {
            httpStatus: response.status,
            timestamp: new Date().toISOString(),
            url: response.url,
        };

        throw responseData;
    }

    return response.json();
}
